import { type HTMLAttributes, type ReactNode, useEffect } from 'react';
import { HelpCircle } from 'lucide-react';

import { cn } from '../../lib/utils';
import { Button } from './button';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

/**
 * Tailwind/shadcn page scaffolding, replacing `Page` / `Header` / `Content` /
 * `ContentHeader` / `HeaderLabel` / `SupportButton` from
 * `@backstage/core-components` (ADR-0012).
 *
 * The names match the components they replace so plugin call sites only change
 * their import source, but `Page` no longer takes `themeId`.
 *
 * There is no banner: the top bar names the page, so `Header` keeps the title
 * for screen readers and the browser tab, and shows only the page's actions.
 */

const PORTAL_TITLE = 'Vivicta Developer Portal';

export const formatDocumentTitle = (pageTitle: string) =>
  `${pageTitle} | ${PORTAL_TITLE}`;

/* Stock Backstage pages set the tab title through react-helmet and leave it
 * behind when they unmount, so every custom page has to set its own. */
const useDocumentTitle = (pageTitle?: string) => {
  useEffect(() => {
    if (pageTitle) document.title = formatDocumentTitle(pageTitle);
  }, [pageTitle]);
};

export const Page = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex min-h-full flex-col', className)} {...props} />
);

export interface HeaderProps {
  title: ReactNode;
  /**
   * Browser tab title. Defaults to `title` when that is a string; set it when
   * `title` is not a string or is not a good name for the page.
   */
  documentTitle?: string;
  /** Read out with the title; not shown. */
  subtitle?: ReactNode;
  /** The page's actions (`SupportButton`, buttons), right-aligned above the content. */
  children?: ReactNode;
  className?: string;
}

export const Header = ({
  title,
  documentTitle,
  subtitle,
  children,
  className,
}: HeaderProps) => {
  useDocumentTitle(
    documentTitle ?? (typeof title === 'string' ? title : undefined),
  );

  return (
    <header className={cn('px-6 md:px-8', className)}>
      <h1 className="sr-only">{title}</h1>
      {subtitle && <p className="sr-only">{subtitle}</p>}
      {/* `empty:hidden`: labels render nothing, so a header of labels takes no room. */}
      <div className="flex flex-wrap items-center justify-end gap-2 pt-4 empty:hidden">
        {children}
      </div>
    </header>
  );
};

export interface HeaderLabelProps {
  label: string;
  value: ReactNode;
}

/** A fact the banner used to show; renders nothing now that there is no banner. */
export const HeaderLabel = (_props: HeaderLabelProps) => null;

export const Content = ({
  className,
  ...props
}: HTMLAttributes<HTMLElement>) => (
  <article className={cn('flex-1 px-6 py-6 md:px-8', className)} {...props} />
);

export interface ContentHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** Right-aligned actions. */
  children?: ReactNode;
  className?: string;
}

export const ContentHeader = ({
  title,
  description,
  children,
  className,
}: ContentHeaderProps) => (
  <div
    className={cn(
      'mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between',
      className,
    )}
  >
    <div className="min-w-0">
      <h2 className="text-xl font-semibold tracking-tight text-foreground">
        {title}
      </h2>
      {description && (
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      )}
    </div>
    {children && (
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    )}
  </div>
);

export interface SupportButtonProps {
  title?: string;
  children?: ReactNode;
}

export const SupportButton = ({
  title = 'Support',
  children,
}: SupportButtonProps) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button variant="ghost" size="sm" aria-label={title}>
        <HelpCircle className="h-4 w-4" />
        {title}
      </Button>
    </PopoverTrigger>
    <PopoverContent align="end" className="w-80 text-sm text-muted-foreground">
      {children}
    </PopoverContent>
  </Popover>
);
